<script lang="ts">
    import { link, replace } from 'svelte-spa-router';
    import BillDetailPanel from '../../lib/Bills/BillDetailPanel.svelte';
    import BillDetailSkeleton from '../../lib/Bills/BillDetailSkeleton.svelte';
    import { billDetailRoute, getBillDetail, type BillDetail } from '../../api/bills';
    import { billsLastBillUri, billsListedUrls, loadBillsContext } from './billsState';
    import * as t from '../../translations/Bills.ts';

    /** `uri` is the bill's relative legislation URL; the router has already URI-decoded it. */
    export let params: { uri?: string } = {};

    let loading = true;
    let detail: BillDetail | null = null;
    let requestedUrl: string | undefined;

    $: void load(params.uri);
    $: billsLastBillUri.value = params.uri ?? '';
    $: index = params.uri ? $billsListedUrls.indexOf(params.uri) : -1;
    $: total = $billsListedUrls.length;
    $: showNavigation = index >= 0 && total > 1;
    $: previousUrl = index > 0 ? $billsListedUrls[index - 1] : null;
    $: nextUrl = index >= 0 && index < total - 1 ? $billsListedUrls[index + 1] : null;

    /** Replace rather than push, so the browser's Back still returns to the list. */
    function goTo(url: string | null) {
        if (url) replace(billDetailRoute(url));
    }

    const focusRing =
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vv-action-center-button-color,#2563eb)] focus-visible:ring-offset-2';
    const iconButton = `inline-flex h-9 w-9 items-center justify-center rounded-lg !border-0 !bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-30 ${focusRing}`;
    const barButton = `inline-flex h-11 min-w-[6.5rem] items-center gap-1.5 rounded-lg !border !border-solid !border-gray-300 bg-white !px-3 text-sm !font-normal text-gray-800 hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40 ${focusRing}`;

    async function load(url: string | undefined) {
        requestedUrl = url;
        loading = true;
        detail = null;
        try {
            if (!url) return;
            // Key legislation is optional here: a failure only loses the position/explanation decoration.
            const spotlights = await loadBillsContext()
                .then((c) => c.spotlights)
                .catch(() => []);
            const loaded = await getBillDetail(url, spotlights);
            if (requestedUrl === url) detail = loaded;
        } catch (e) {
            console.error('[Bills] Failed to load bill detail:', e);
        } finally {
            if (requestedUrl === url) loading = false;
        }
        window.scrollTo?.({ top: 0 });
    }
</script>

<div class="w-full m-auto max-w-[1000px] py-4 px-4 md:px-8 interfont {showNavigation ? 'pb-24 sm:pb-4' : ''}" data-testid="bill-detail-page">
    <div class="mb-6 flex min-h-9 items-center justify-between gap-3">
        <a
            href="/"
            use:link
            class="inline-flex items-center gap-2 rounded-md px-1.5 py-1 !text-gray-600 transition-colors hover:!text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vv-action-center-button-color,#2563eb)] focus-visible:ring-offset-2"
        >
            <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span class="text-base font-medium underline-offset-2 hover:underline">{t.Back_To_Bills()}</span>
        </a>

        {#if showNavigation}
            <nav class="hidden items-center gap-1 sm:flex" aria-label={t.Bill_Navigation_Label()} data-testid="bill-navigation">
                <span class="mr-1 text-sm tabular-nums text-gray-500" data-testid="bill-navigation-position">
                    {t.Bill_Position_Of(String(index + 1), String(total))}
                </span>
                <button type="button" class={iconButton} disabled={!previousUrl} aria-label={t.Previous_Bill()} on:click={() => goTo(previousUrl)}>
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <button type="button" class={iconButton} disabled={!nextUrl} aria-label={t.Next_Bill()} on:click={() => goTo(nextUrl)}>
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </nav>
        {/if}
    </div>

    {#if loading}
        <div aria-busy="true" aria-label={t.Loading()}>
            <BillDetailSkeleton />
        </div>
    {:else if detail}
        <div class="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <BillDetailPanel {detail} />
        </div>
    {:else}
        <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            {t.Detail_Error()}
        </div>
    {/if}
</div>

{#if showNavigation}
    <nav
        class="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-2 border-0 border-t border-solid border-gray-200 bg-white/95 px-4 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-[0_-4px_12px_rgba(0,0,0,0.06)] backdrop-blur interfont sm:hidden"
        aria-label={t.Bill_Navigation_Label()}
        data-testid="bill-navigation-mobile"
    >
        <button type="button" class={barButton} disabled={!previousUrl} aria-label={t.Previous_Bill()} on:click={() => goTo(previousUrl)}>
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            {t.Previous()}
        </button>
        <span class="text-sm tabular-nums text-gray-600">{t.Bill_Position_Of(String(index + 1), String(total))}</span>
        <button type="button" class="{barButton} justify-end" disabled={!nextUrl} aria-label={t.Next_Bill()} on:click={() => goTo(nextUrl)}>
            {t.Next()}
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
        </button>
    </nav>
{/if}
